import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, Calendar, Truck, Package, X, Loader2 } from 'lucide-react';
import { fetchFulfillmentOrders, updateFulfillment } from '../lib/api';
import type { Order } from '../lib/types';

const Fulfillment: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [verifiedItems, setVerifiedItems] = useState<Set<string>>(new Set());
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const data = await fetchFulfillmentOrders();
      setOrders(data);
      setError(null);
    } catch (err) {
      setError('Failed to load orders');
      console.error('Error loading orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityFulfilledChange = async (orderId: string, itemId: string, quantity: number) => {
    const newQuantity = Math.max(0, quantity);
    
    // Find the current order and item
    const currentOrder = orders.find(order => order.id === orderId);
    const currentItem = currentOrder?.items.find(item => item.id === itemId);
    
    if (!currentOrder || !currentItem) return;
    
    // Calculate the valid quantity (not exceeding ordered or available)
    const maxQuantity = Math.min(currentItem.quantity, currentItem.product.qoh);
    const validQuantity = Math.min(newQuantity, maxQuantity);
    
    // Update local state with validated quantity
    setOrders(prevOrders => 
      prevOrders.map(order => {
        if (order.id !== orderId) return order;
        
        return {
          ...order,
          items: order.items.map(item => {
            if (item.id !== itemId) return item;
            return {
              ...item,
              quantityFulfilled: validQuantity
            };
          })
        };
      })
    );

    try {
      // Send the validated quantity to the API
      await updateFulfillment(orderId, [{
        id: itemId,
        quantityFulfilled: validQuantity // Use the validated quantity
      }]);
    } catch (err) {
      console.error('Error updating fulfillment:', err);
      setError('Failed to update quantity');
      await loadOrders(); // Reload to get correct state
    }
  };

  const handleVerifyItem = (itemId: string) => {
    const newVerifiedItems = new Set(verifiedItems);
    if (newVerifiedItems.has(itemId)) {
      newVerifiedItems.delete(itemId);
    } else {
      newVerifiedItems.add(itemId);
    }
    setVerifiedItems(newVerifiedItems);
  };

  const isItemReadyForVerification = (item: any): boolean => {
    return item.quantityFulfilled > 0 && 
           item.quantityFulfilled <= item.quantity && 
           item.quantityFulfilled <= item.product.qoh;
  };

  const isOrderFullyVerified = (order: Order): boolean => {
    return order.items.every(item => 
      verifiedItems.has(item.id) && 
      isItemReadyForVerification(item)
    );
  };

  const handleConfirmFulfillment = (order: Order) => {
    if (!isOrderFullyVerified(order)) {
      setError('Please verify all items before confirming fulfillment');
      return;
    }
    setSelectedOrder(order);
    setShowConfirmation(true);
  };

  const completeFulfillment = async () => {
    if (!selectedOrder) return;

    try {
      setSubmitting(true);
      setError(null);

      await updateFulfillment(
        selectedOrder.id,
        selectedOrder.items.map(item => ({
          id: item.id,
          quantityFulfilled: item.quantityFulfilled
        }))
      );

      await loadOrders();
      setSelectedOrder(null);
      setShowConfirmation(false);
      setVerifiedItems(new Set());
    } catch (err) {
      setError('Failed to complete fulfillment');
      console.error('Error completing fulfillment:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Orders</h2>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={loadOrders}
            className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Fulfillment</h2>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {orders.map((order) => (
          <div key={order.id} className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{order.customerName}</h3>
                  <p className="text-gray-600">Order #{order.id}</p>
                  {order.notes && (
                    <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded-md p-3">
                      <p className="text-sm font-medium text-yellow-800">Delivery Notes:</p>
                      <p className="text-sm text-yellow-700 mt-1">{order.notes}</p>
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span>Created: {new Date(order.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quantity Ordered</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quantity Fulfilled</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Available</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Verify</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {order.items.map((item) => {
                      const isVerified = verifiedItems.has(item.id);
                      const hasEnoughStock = item.product.qoh >= item.quantity;
                      const isReadyForVerification = isItemReadyForVerification(item);

                      return (
                        <tr key={item.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {item.product.product}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                            {item.quantity}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <input
                              type="number"
                              min="0"
                              max={Math.min(item.quantity, item.product.qoh)}
                              value={item.quantityFulfilled || ''}
                              onChange={(e) => handleQuantityFulfilledChange(order.id, item.id, parseInt(e.target.value))}
                              onBlur={() => loadOrders()}
                              className="w-24 px-2 py-1 text-right border rounded-md focus:ring-green-500 focus:border-green-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              placeholder="QTY"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="flex flex-col items-end">
                              <span className="text-sm text-green-600">
                                {item.product.qoh} QOH
                              </span>
                              <span className="text-sm text-red-600">
                                {item.product.pendingOrders} pending
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            {isReadyForVerification ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Ready to Verify
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                Pending Fulfillment
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <button
                              onClick={() => handleVerifyItem(item.id)}
                              disabled={!isReadyForVerification}
                              className={`p-2 rounded-full transition-colors ${
                                isVerified
                                  ? 'bg-green-100 text-green-600 hover:bg-green-200'
                                  : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                              } ${!isReadyForVerification ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <CheckCircle2 className="w-5 h-5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mt-6 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Package className="w-5 h-5 text-gray-400" />
                  <span className="text-gray-600">
                    {verifiedItems.size} of {order.items.length} items verified
                  </span>
                </div>
                <button
                  onClick={() => handleConfirmFulfillment(order)}
                  disabled={!isOrderFullyVerified(order)}
                  className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  <Truck className="w-5 h-5" />
                  <span>Mark Ready for Delivery</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showConfirmation && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Fulfillment</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to mark this order as ready for delivery? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowConfirmation(false)}
                className="px-4 py-2 border rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={completeFulfillment}
                disabled={submitting}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Confirm'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {orders.length === 0 && (
        <div className="text-center py-12">
          <div className="bg-gray-50 rounded-lg p-6 inline-block">
            <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Orders to Fulfill</h3>
            <p className="text-gray-600">
              There are currently no orders in the fulfillment queue.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Fulfillment;